import React, { useState, useEffect, useRef, useMemo } from 'react';
import backgroundData from './utils/backgroundData.json';
import adityantaLogo from './assets/adityanta-logo.jpeg';
import {
  LayoutDashboard,
  ShoppingCart,
  Users,
  CreditCard,
  Search,
  Upload,
  MoreVertical,
  X,
  FileText,
  Loader2,
  Filter,
  ArrowUpDown,
  CheckCircle,
  LogOut,
  UploadCloud,
  Pencil,
  Trash2,
  Plus,
  CircleOff
} from 'lucide-react';

// --- CONFIGURATION ---
// Backend URL comes from admin-frontend/.env (generated from config/urls.json).
// When ngrok is on, this is the public backend URL so a remote admin UI can reach the API.
const BASE_URL = (typeof window !== 'undefined' && window.__APP_CONFIG__ && window.__APP_CONFIG__.API_BASE_URL)
  || process.env.REACT_APP_API_BASE_URL
  || 'http://localhost:3001';
const NGROK_HEADERS = String(BASE_URL).includes('ngrok')
  ? { 'ngrok-skip-browser-warning': 'true' }
  : {};

const normalizeTopicForBackground = (topic) => {
  const value = `${topic || ''}`.trim().toLowerCase();
  const topicMap = {
    business: 'Business',
    economics: 'Economics',
    history: 'History',
    geography: 'Geography',
    science: 'Science',
    physics: 'Science',
    chemistry: 'Science',
    biology: 'Science',
    'environment and ecology': 'Science',
    computer: 'Technology & Computer Subjects',
    'current affairs': 'Generic',
    'bihar special': 'Generic',
    reasoning: 'Generic',
    'mix gk/gs': 'Generic',
    marketing: 'Marketing',
    'legal studies': 'Legal Studies',
    'political science': 'Political Science',
    'music and dance': 'Music and dance',
    'technology & computer subjects': 'Technology & Computer Subjects',
    'physical & skill subjects': 'Physical & Skill Subjects',
    mathematics: 'Maths',
    maths: 'Maths',
    math: 'Maths',
    finance: 'Finance',
    'financial markets management': 'Finance',
    'fine arts / painting': 'Fine Arts - Painting',
    'fine arts - painting': 'Fine Arts - Painting',
    generic: 'Generic',
    general: 'Generic',
  };
  return topicMap[value] || topic || 'Generic';
};

const isUrlLike = (value) => typeof value === 'string' && /^(https?:\/\/|www\.)/i.test(value.trim());

const normalizeTemplateImageUrl = (value) => {
  const raw = `${value || ''}`.trim();
  if (!raw) return '';
  if (/^www\./i.test(raw)) return `https://${raw}`;
  if (/^http:\/\//i.test(raw)) return raw.replace(/^http:\/\//i, 'https://');
  return raw;
};

const isUsableTemplateImage = (url) => {
  if (!url || !isUrlLike(url)) return false;
  if (/googleusercontent\.com\/image_collection\/image_retrieval\//i.test(url)) return false;
  if (/via\.placeholder\.com/i.test(url)) return false;
  if (/\.pptx?($|\?)/i.test(url)) return false;
  return true;
};

const getTemplatePreviewImage = (template) => {
  const fromThumbnail = normalizeTemplateImageUrl(template?.thumbnail_url || template?.thumbnailUrl);
  if (isUsableTemplateImage(fromThumbnail)) return fromThumbnail;
  const fromPreview = normalizeTemplateImageUrl(template?.preview);
  if (isUsableTemplateImage(fromPreview)) return fromPreview;
  return '';
};

const getTemplateFallbackBackground = (template) => {
  const normalizedTopic = normalizeTopicForBackground(template?.topic);
  const options = backgroundData?.[normalizedTopic] || backgroundData?.Generic || [];
  if (!Array.isArray(options) || options.length === 0) return '';
  const seed = `${template?.template_id || template?.id || template?.title || ''}`;
  const hash = [...seed].reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
  return options[hash % options.length];
};

/** Admin template upload / edit — topic must be one of these (matches customer background folders). */
const ADMIN_TEMPLATE_TOPIC_OPTIONS = [
  'Literature',
  'Mathematics',
  'History',
  'Geography',
  'Political Science',
  'Economics',
  'Technology & Computer Subjects',
  'Legal Studies',
  'Marketing',
  'Financial Markets Management',
  'Fine Arts / Painting',
  'Music and dance',
  'Business',
  'Physical & Skill Subjects',
  'Generic',
  'Current affairs',
  'Computer',
  'Bihar Special',
  'Reasoning',
  'Mix GK/GS',
  'Environment and ecology',
  'Physics',
  'Chemistry',
  'Biology',
];

// --- MOCK DATA (Fallback if API fails) ---
const MOCK_DATA = {
  dashboard: {
    total_earnings: 43263,
    total_users: 432,
    total_templates: 1242,
    active_memberships: 12
  },
  payments: [
    { user_id: '267400', name: 'Sham Kumar', mobile: '+91 8555816729', plan: 'Regular', end_date: '12 Oct \'25' },
    { user_id: '651535', name: 'Devdas B', mobile: '+91 7555315405', plan: 'Business', end_date: '12 Oct \'25' },
    { user_id: '558612', name: 'Param N', mobile: '+91 855 5621531', plan: 'Regular', end_date: '2 Jan \'26' },
    { user_id: '449003', name: 'Hema Chaudhari', mobile: '+91 9755540892', plan: 'Business', end_date: '12 Oct \'25' },
    { user_id: '651535', name: 'Lekha D', mobile: '+91 9355531660', plan: 'Regular', end_date: '12 Jan \'26' },
    { user_id: '558612', name: 'Ayush Dhyan', mobile: '+91 9055533468', plan: 'Regular', end_date: '7 Mar \'26' },
  ],
  templates: [
    { template_id: '1', title: 'BioScience Report', topic: 'Science', frames: 12, downloads: '1.2k', license: 'FREE', is_store_item: true, image: 'https://via.placeholder.com/300x180/e0f2f1/00695c?text=BioScience' },
    { template_id: '2', title: 'Chem Insights', topic: 'Science', frames: 8, downloads: '80', license: 'FREE', is_store_item: true, image: 'https://via.placeholder.com/300x180/e3f2fd/1565c0?text=Chemistry' },
    { template_id: '3', title: 'Physic Study', topic: 'Science', frames: 7, downloads: '533', license: 'FREE', is_store_item: true, image: 'https://via.placeholder.com/300x180/fff3e0/ef6c00?text=Physics' },
    { template_id: '4', title: 'The Ecology', topic: 'Science', frames: 4, downloads: '15', license: 'FREE', is_store_item: true, image: 'https://via.placeholder.com/300x180/f3e5f5/7b1fa2?text=Ecology' },
    { template_id: '5', title: 'Math Model', topic: 'Science', frames: 12, downloads: '2.5k', license: 'FREE', is_store_item: true, image: 'https://via.placeholder.com/300x180/e8f5e9/2e7d32?text=Math+Model' },
    { template_id: '6', title: 'Scientific Design', topic: 'Science', frames: 11, downloads: '64', license: 'FREE', is_store_item: true, image: 'https://via.placeholder.com/300x180/ffebee/c62828?text=Design' },
  ],
  users: [
    { user_id: '267400', mobile: '+91 8555816729', name: '-', downloads: 6 },
    { user_id: '651535', mobile: '+91 7555315405', name: 'Devdas B', downloads: 1 },
    { user_id: '558612', mobile: '+91 855 5621531', name: '-', downloads: 1 },
    { user_id: '449003', mobile: '+91 9755540892', name: 'Hema Chaudhari', downloads: 4 },
    { user_id: '651535', mobile: '+91 9355531660', name: 'Lekha D', downloads: 2 },
  ],
  pricing: [
    { id: 'mock-weekly', name: 'Weekly', amount: 9900, currency: 'INR', duration_unit: 'WEEKLY', duration_value: 1, duration_label: '1 week', is_active: true, can_delete: false, disabled_at: null },
    { id: 'mock-monthly', name: 'Monthly', amount: 29900, currency: 'INR', duration_unit: 'MONTHLY', duration_value: 1, duration_label: '1 month', is_active: true, can_delete: false, disabled_at: null },
    { id: 'mock-quarterly', name: 'Quarterly', amount: 79900, currency: 'INR', duration_unit: 'MONTHLY', duration_value: 3, duration_label: '3 months', is_active: true, can_delete: false, disabled_at: null },
  ]
};

// --- API HELPER ---
const apiCall = async (endpoint, method = 'GET', body = null, token = null) => {
  const headers = { 'Content-Type': 'application/json', ...NGROK_HEADERS };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  try {
    const response = await fetch(`${BASE_URL}${endpoint}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : null,
    });
    
    const contentType = response.headers.get("content-type") || '';
    let data = null;
    if (contentType.includes('application/json')) {
      data = await response.json();
    } else {
      const text = await response.text();
      data = text ? { success: false, message: text } : null;
    }
    if (!response.ok) {
      return data || { success: false, message: `Request failed (${response.status})` };
    }
    return data || { success: true };
  } catch (error) {
    console.warn(`API Failed for ${endpoint}, falling back to mock data if available.`);
    return null;
  }
};

// --- COMPONENTS ---

const Card = ({ children, className = "" }) => (
  <div className={`bg-white rounded-xl border border-gray-100 shadow-sm p-5 ${className}`}>
    {children}
  </div>
);

const Button = ({ children, variant = 'primary', className = "", onClick, type = "button", disabled = false }) => {
  const baseStyle = "px-4 py-2.5 rounded-lg font-medium transition-all text-sm flex items-center justify-center gap-2";
  const variants = {
    primary: "bg-[#74a88e] hover:bg-[#5e8c75] text-white shadow-md shadow-[#74a88e]/20",
    secondary: "bg-orange-500 hover:bg-orange-600 text-white shadow-md shadow-orange-500/20",
    outline: "border border-gray-300 text-gray-700 hover:bg-gray-50",
    ghost: "text-gray-500 hover:bg-gray-100 hover:text-gray-700",
    danger: "bg-red-500 text-white hover:bg-red-600"
  };

  return (
    <button 
      type={type} 
      className={`${baseStyle} ${variants[variant]} ${className} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </button>
  );
};

const Input = ({ label, type = "text", placeholder, value, onChange, name, className = "" }) => (
  <div className={`flex flex-col gap-1.5 ${className}`}>
    {label && <label className="text-sm font-semibold text-gray-700">{label}</label>}
    <input 
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className="w-full px-4 py-2.5 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#74a88e]/50 text-sm transition-all"
    />
  </div>
);

const Badge = ({ children, type = 'success' }) => {
  const styles = {
    success: 'bg-green-100 text-green-700',
    warning: 'bg-yellow-100 text-yellow-700',
    neutral: 'bg-gray-100 text-gray-700',
    accent: 'bg-orange-100 text-orange-800 border border-orange-200',
  };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-bold ${styles[type]}`}>
      {children}
    </span>
  );
};

// --- MAIN APP ---

export default function App() {
  const [token, setToken] = useState(localStorage.getItem('admin_token'));
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);

  // Global State for different Views
  const [dashboardData, setDashboardData] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [users, setUsers] = useState([]);
  const [pricing, setPricing] = useState([]);
  const [payments, setPayments] = useState([]);

  // Modal States
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [currentUpload, setCurrentUpload] = useState(null); // File being uploaded
  const [editingTemplate, setEditingTemplate] = useState(null); // Template details being edited

  // Fetch Data Effects
  useEffect(() => {
    if (token) {
      fetchDashboard();
      if (activeTab === 'templates') fetchTemplates();
      if (activeTab === 'users') fetchUsers();
      if (activeTab === 'subscription') fetchPricing();
    }
  }, [token, activeTab]);

  const handleLogin = async (username, password) => {
    setLoading(true);
    // Try API
    const res = await apiCall('/api/v1/admin/auth/login', 'POST', { username, password });
    
    if (res && res.success) {
      setToken(res.token);
      localStorage.setItem('admin_token', res.token);
    } else if (username === 'admin' && password === 'admin') {
      // Fallback Mock Login
      const mockToken = 'mock_jwt_token_123';
      setToken(mockToken);
      localStorage.setItem('admin_token', mockToken);
    } else {
      alert('Invalid credentials');
    }
    setLoading(false);
  };

  const logout = () => {
    setToken(null);
    localStorage.removeItem('admin_token');
  };

  const fetchDashboard = async () => {
    const res = await apiCall('/api/v1/admin/dashboard', 'GET', null, token);
    if (res && res.success) {
      // Transform API response to match expected format
      const dashboard = res.dashboard;
      setDashboardData({
        total_earnings: dashboard.total_earnings_display ? dashboard.total_earnings_display.replace('₹', '').replace(',', '') : dashboard.total_earnings,
        total_users: dashboard.total_users,
        total_templates: dashboard.total_templates,
        active_memberships: dashboard.active_memberships
      });
    } else {
      setDashboardData(MOCK_DATA.dashboard);
    }

    // Usually payments are separate, but mocking here for dashboard table
    const payRes = await apiCall('/api/v1/admin/payments', 'GET', null, token);
    if (payRes && payRes.success) {
      // Transform API response to match table format
      const transformedPayments = payRes.payments.map(payment => ({
        user_id: payment.payment_id, // Using payment_id as user_id for display
        name: payment.customer_name,
        mobile: payment.customer_phone,
        email: payment.customer_email,
        plan: payment.duration_label ? `${payment.plan} · ${payment.duration_label}` : payment.plan,
        end_date: payment.expires_at
          ? new Date(payment.expires_at).toLocaleDateString()
          : (payment.payment_date ? new Date(payment.payment_date).toLocaleDateString() : 'N/A')
      }));
      setPayments(transformedPayments);
    } else {
      setPayments(MOCK_DATA.payments);
    }
  };

  const fetchTemplates = async () => {
    const res = await apiCall('/api/v1/admin/templates', 'GET', null, token);
    if (res && res.success) {
      // Transform API response to match expected format
      const transformedTemplates = res.templates.map(template => ({
        template_id: template.template_id,
        title: template.title,
        topic: template.topic,
        sub_topic: template.sub_topic || '',
        frames: template.frames,
        downloads: Number(template.downloads) || 0,
        created_at: template.created_at || null,
        license: template.license,
        is_store_item: Boolean(template.is_store_item),
        thumbnail_url: template.thumbnailUrl || template.thumbnail_url || '',
        preview: template.preview || ''
      }));
      setTemplates(transformedTemplates);
    } else {
      setTemplates(MOCK_DATA.templates);
    }
  };

  const fetchUsers = async () => {
    const res = await apiCall('/api/v1/admin/users', 'GET', null, token);
    if (res && res.success) {
      // Transform API response to match expected format
      const transformedUsers = res.users.map(user => ({
        user_id: user.user_id,
        mobile: user.phone,
        name: user.name,
        downloads: user.downloads_made
      }));
      setUsers(transformedUsers);
    } else {
      setUsers(MOCK_DATA.users);
    }
  };

  const fetchPricing = async () => {
    const res = await apiCall('/api/v1/admin/pricing', 'GET', null, token);
    if (res && res.success) {
      setPricing(res.pricing || []);
    } else {
      setPricing([]);
    }
  };

  // --- RENDERERS ---

  if (!token) {
    return <LoginPage onLogin={handleLogin} loading={loading} />;
  }

  return (
    <div className="flex h-screen bg-[#FDFCFB] text-gray-800 font-sans">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-gray-100 flex flex-col fixed h-full z-10">
        <div className="p-6 pb-8">
          <img src={adityantaLogo} alt="Adityanta" className="w-full h-auto" />
        </div>

        <nav className="flex-1 px-4 space-y-2">
          <NavItem 
            icon={<LayoutDashboard size={20} />} 
            label="Overview" 
            active={activeTab === 'overview'} 
            onClick={() => setActiveTab('overview')} 
          />
          <NavItem 
            icon={<ShoppingCart size={20} />} 
            label="Templates" 
            active={activeTab === 'templates'} 
            onClick={() => setActiveTab('templates')} 
          />
          <NavItem 
            icon={<Users size={20} />} 
            label="Users" 
            active={activeTab === 'users'} 
            onClick={() => setActiveTab('users')} 
          />
          <NavItem 
            icon={<CreditCard size={20} />} 
            label="Subscription" 
            active={activeTab === 'subscription'} 
            onClick={() => setActiveTab('subscription')} 
          />
        </nav>

        <div className="p-4 border-t border-gray-100">
          <button onClick={logout} className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-gray-500 hover:text-red-500 transition-colors w-full">
            <LogOut size={18} />
            Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 ml-64 flex flex-col h-screen overflow-hidden">
        {/* Header */}
        <header className="h-20 px-8 flex items-center justify-between bg-white border-b border-gray-50 shrink-0">
          <h1 className="text-xl font-medium text-gray-600 capitalize">{activeTab}</h1>
          <div className="relative w-96">
            <input 
              type="text" 
              placeholder="Search anything" 
              className="w-full pl-4 pr-10 py-2.5 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-gray-400"
            />
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          </div>
        </header>

        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-auto p-8">
          {activeTab === 'overview' && (
            <Overview dashboardData={dashboardData} payments={payments} />
          )}
          {activeTab === 'templates' && (
            <Templates 
              templates={templates} 
              onUploadClick={() => setIsUploadModalOpen(true)} 
              onEditClick={(tpl) => { setEditingTemplate(tpl); setIsDetailModalOpen(true); }}
              onDelete={async (templateId) => {
                if (!window.confirm('Are you sure you want to delete this template?')) return;
                try {
                  const res = await apiCall(`/api/v1/admin/templates/${templateId}`, 'DELETE', null, token);
                  if (res && res.success) {
                    fetchTemplates();
                  } else {
                    alert('Failed to delete: ' + (res?.message || 'Unknown error'));
                  }
                } catch (err) {
                  alert('Failed to delete template.');
                }
              }}
            />
          )}
          {activeTab === 'users' && (
            <UsersList users={users} />
          )}
          {activeTab === 'subscription' && (
            <Subscription pricing={pricing} token={token} refresh={fetchPricing} />
          )}
        </div>
      </main>

      {/* Modals */}
      {isUploadModalOpen && (
        <UploadModal 
          onClose={() => setIsUploadModalOpen(false)} 
          onFileSelect={(file) => {
            setCurrentUpload(file);
            setIsUploadModalOpen(false);
            // Open Details Modal with default empty state + file name
            setEditingTemplate({ title: file.name.split('.')[0], topic: '', license: 'FREE', about: '', is_store_item: true });
            setIsDetailModalOpen(true);
          }} 
        />
      )}

      {isDetailModalOpen && (
        <TemplateDetailModal 
          template={editingTemplate}
          isNew={!!currentUpload}
          loading={loading}
          onClose={() => {
            setIsDetailModalOpen(false);
            setCurrentUpload(null);
            setEditingTemplate(null);
          }}
          onBack={() => {
            setIsDetailModalOpen(false);
            setEditingTemplate(null);
            setIsUploadModalOpen(true);
          }}
          onSave={async (data) => {
            setLoading(true);
            try {
              if (currentUpload) {
                const formData = new FormData();
                formData.append('file', currentUpload);
                formData.append('title', data.title);
                formData.append('topic', data.topic);
                formData.append('license', data.license);
                formData.append('frames', '10');
                formData.append('is_store_item', data.is_store_item ? 'true' : 'false');
                if (data.is_store_item && data.sub_topic) {
                  formData.append('sub_topic', data.sub_topic.trim());
                }

                const response = await fetch(`${BASE_URL}/api/v1/admin/templates`, {
                  method: 'POST',
                  headers: {
                    'Authorization': `Bearer ${token}`,
                    ...NGROK_HEADERS,
                  },
                  body: formData,
                });

                const result = await response.json();

                if (result.success) {
                  fetchTemplates();
                  setIsDetailModalOpen(false);
                  setCurrentUpload(null);
                  setEditingTemplate(null);
                } else {
                  alert('Upload failed: ' + (result.message || 'Unknown error'));
                }
              } else if (editingTemplate?.template_id) {
                const result = await apiCall(
                  `/api/v1/admin/templates/${editingTemplate.template_id}`,
                  'PATCH',
                  {
                    title: data.title?.trim(),
                    topic: data.topic?.trim(),
                    license: data.license,
                    is_store_item: !!data.is_store_item,
                    sub_topic: data.is_store_item ? (data.sub_topic?.trim() || null) : null,
                  },
                  token
                );
                if (result && result.success) {
                  fetchTemplates();
                  setIsDetailModalOpen(false);
                  setEditingTemplate(null);
                } else {
                  alert('Update failed: ' + (result?.message || 'Unknown error'));
                }
              }
            } catch (error) {
              console.error('Save error:', error);
              alert('Request failed. Please try again.');
            }
            setLoading(false);
          }}
        />
      )}
    </div>
  );
}

// --- SUB-COMPONENTS ---

const NavItem = ({ icon, label, active, onClick }) => (
  <button 
    onClick={onClick}
    className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium transition-all rounded-r-lg relative ${
      active 
        ? 'text-orange-500 bg-orange-50' 
        : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
    }`}
  >
    {/* Active Indicator Line */}
    {active && <div className="absolute left-0 top-0 bottom-0 w-1 bg-orange-500 rounded-r"></div>}
    {icon}
    {label}
  </button>
);

const LoginPage = ({ onLogin, loading }) => {
  const [user, setUser] = useState('');
  const [pass, setPass] = useState('');

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FDFCFB]">
      <div className="w-full max-w-md bg-white rounded-2xl p-10 text-center shadow-xl shadow-gray-100/50 border border-gray-100">
        <div className="flex flex-col items-center mb-8">
          <img src={adityantaLogo} alt="Adityanta" className="w-56 h-auto" />
        </div>

        <div className="space-y-6 text-left">
          <Input 
            label="User Name" 
            placeholder="Enter User name" 
            value={user} 
            onChange={(e) => setUser(e.target.value)} 
          />
          <Input 
            label="Password" 
            type="password" 
            placeholder="Enter Password" 
            value={pass} 
            onChange={(e) => setPass(e.target.value)} 
          />
          
          <button 
            onClick={() => onLogin(user, pass)}
            disabled={loading}
            className="w-full bg-[#82b59c] hover:bg-[#74a88e] text-white py-3.5 rounded-xl font-semibold shadow-lg shadow-[#82b59c]/30 transition-all flex justify-center items-center"
          >
            {loading ? <Loader2 className="animate-spin" /> : 'Continue'}
          </button>
        </div>
      </div>
    </div>
  );
};

const Overview = ({ dashboardData, payments }) => {
  if (!dashboardData) return <div className="p-10 text-center"><Loader2 className="animate-spin mx-auto"/></div>;

  return (
    <div className="space-y-8 animate-fade-in">
      <div className="mb-4">
        <h2 className="text-2xl font-bold text-gray-800">Overview</h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatCard 
          label="Earnings" 
          value={`₹ ${dashboardData.total_earnings}`} 
          trend="-2.1%" 
          trendUp={false} 
        />
        <StatCard 
          label="Users" 
          value={dashboardData.total_users} 
          trend="+1.1%" 
          trendUp={true} 
        />
        <StatCard 
          label="Templates Copies" 
          value={dashboardData.total_templates} 
          trend="+2.1%" 
          trendUp={true} 
        />
      </div>

      <div>
        <h3 className="text-lg font-bold text-gray-800 mb-4">Subscription Purchases</h3>
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <table className="w-full text-left text-sm">
            <thead className="bg-[#EBEAE8] text-gray-700">
              <tr>
                <th className="px-6 py-4 font-medium">User ID</th>
                <th className="px-6 py-4 font-medium">Name</th>
                <th className="px-6 py-4 font-medium">Mobile, Email</th>
                <th className="px-6 py-4 font-medium">Plan</th>
                <th className="px-6 py-4 font-medium">Membership ends</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {payments.map((p, i) => (
                <tr key={i} className="hover:bg-gray-50/50">
                  <td className="px-6 py-4 text-gray-600">{p.user_id}</td>
                  <td className="px-6 py-4 font-medium text-gray-800">{p.name}</td>
                  <td className="px-6 py-4 text-gray-600">{p.mobile || p.email}</td>
                  <td className="px-6 py-4 text-gray-800">{p.plan}</td>
                  <td className="px-6 py-4 text-gray-600">{p.end_date || p.expires_at}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ label, value, trend, trendUp }) => (
  <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm flex flex-col gap-4">
    <div className="flex items-center gap-2 text-gray-400 text-sm font-medium">
      {label === 'Earnings' ? <CreditCard size={16}/> : label === 'Users' ? <Users size={16}/> : <ShoppingCart size={16}/>}
      {label}
    </div>
    <div className="flex items-end gap-3">
      <span className="text-3xl font-bold text-gray-900">{value}</span>
      <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${trendUp ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
        {trend}
      </span>
    </div>
  </div>
);

const ADMIN_PAGE_SIZE = 24;

const pageList = (page, pageCount) => {
  const pages = new Set([1, pageCount, page - 1, page, page + 1]);
  if (page <= 3) [2, 3, 4].forEach((p) => pages.add(p));
  if (page >= pageCount - 2) [pageCount - 1, pageCount - 2, pageCount - 3].forEach((p) => pages.add(p));
  const sorted = [...pages].filter((p) => p >= 1 && p <= pageCount).sort((a, b) => a - b);
  const out = [];
  sorted.forEach((p, i) => {
    if (i && p - sorted[i - 1] > 1) out.push(`gap-${p}`);
    out.push(p);
  });
  return out;
};

const Pagination = ({ page, pageCount, onChange }) => {
  if (!pageCount || pageCount <= 1) return null;
  const go = (p) => {
    const next = Math.min(pageCount, Math.max(1, p));
    if (next !== page) onChange(next);
  };
  const arrow = 'flex items-center gap-1 px-3 h-10 rounded-xl border text-sm font-semibold transition-all disabled:opacity-40 disabled:cursor-not-allowed';
  return (
    <nav aria-label="Pages" className="mt-8 mb-20 flex flex-col items-center gap-3">
      <div className="flex flex-wrap items-center justify-center gap-2">
        <button type="button" onClick={() => go(page - 1)} disabled={page <= 1} className={`${arrow} border-gray-200 bg-white text-gray-700 hover:bg-gray-50`}>
          Prev
        </button>
        {pageList(page, pageCount).map((p) =>
          typeof p === 'string' ? (
            <span key={p} className="w-6 text-center text-gray-400 select-none">…</span>
          ) : (
            <button
              key={p}
              type="button"
              onClick={() => go(p)}
              className={`min-w-[2.5rem] h-10 px-3 rounded-xl text-sm font-semibold ${
                p === page ? 'bg-[#0F5132] text-white' : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
              }`}
            >
              {p}
            </button>
          )
        )}
        <button type="button" onClick={() => go(page + 1)} disabled={page >= pageCount} className={`${arrow} border-gray-200 bg-white text-gray-700 hover:bg-gray-50`}>
          Next
        </button>
      </div>
      <p className="text-sm text-gray-500">
        Page <span className="font-semibold text-gray-800">{page}</span> of <span className="font-semibold text-gray-800">{pageCount}</span>
      </p>
    </nav>
  );
};

const Templates = ({ templates, onUploadClick, onEditClick, onDelete }) => {
  const [topicFilter, setTopicFilter] = useState('All');
  const [licenseFilter, setLicenseFilter] = useState('All');
  const [sortBy, setSortBy] = useState('newest');
  const [page, setPage] = useState(1);

  const topicOptions = ['All', ...Array.from(new Set(templates.map((t) => t.topic).filter(Boolean))).sort((a, b) => a.localeCompare(b))];

  const visible = useMemo(() => templates
    .filter((t) => (topicFilter === 'All' || t.topic === topicFilter) && (licenseFilter === 'All' || String(t.license || '').toUpperCase() === licenseFilter))
    .slice()
    .sort((a, b) => {
      if (sortBy === 'oldest') return new Date(a.created_at || 0) - new Date(b.created_at || 0);
      if (sortBy === 'title') return String(a.title || '').localeCompare(String(b.title || ''));
      if (sortBy === 'downloads') return (Number(b.downloads) || 0) - (Number(a.downloads) || 0);
      return new Date(b.created_at || 0) - new Date(a.created_at || 0);
    }), [templates, topicFilter, licenseFilter, sortBy]);

  const pageCount = Math.max(1, Math.ceil(visible.length / ADMIN_PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const paged = visible.slice((currentPage - 1) * ADMIN_PAGE_SIZE, currentPage * ADMIN_PAGE_SIZE);

  useEffect(() => { setPage(1); }, [topicFilter, licenseFilter, sortBy]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <h2 className="text-lg font-bold">Templates ({visible.length})</h2>
        <div className="flex gap-3 flex-wrap">
          <label className="flex items-center gap-2 text-xs text-gray-500">
            Topic
            <select value={topicFilter} onChange={(e) => setTopicFilter(e.target.value)} className="h-8 px-2 rounded-lg border border-gray-200 bg-white text-gray-800">
              {topicOptions.map((topic) => <option key={topic} value={topic}>{topic}</option>)}
            </select>
          </label>
          <label className="flex items-center gap-2 text-xs text-gray-500">
            Licence
            <select value={licenseFilter} onChange={(e) => setLicenseFilter(e.target.value)} className="h-8 px-2 rounded-lg border border-gray-200 bg-white text-gray-800">
              <option value="All">All</option>
              <option value="FREE">FREE</option>
              <option value="PAID">PAID</option>
            </select>
          </label>
          <label className="flex items-center gap-2 text-xs text-gray-500">
            Sort
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="h-8 px-2 rounded-lg border border-gray-200 bg-white text-gray-800">
              <option value="newest">Newest</option>
              <option value="oldest">Oldest</option>
              <option value="title">Title A–Z</option>
              <option value="downloads">Views</option>
            </select>
          </label>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {paged.map((t) => {
          const previewImage = getTemplatePreviewImage(t);
          const fallbackImage = getTemplateFallbackBackground(t);
          const resolvedImage = previewImage || fallbackImage;
          return (
          <div key={t.template_id} className="group bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-lg transition-all cursor-pointer" onClick={() => onEditClick(t)}>
            <div className="relative h-40 bg-gray-100 overflow-hidden">
              {resolvedImage ? (
                <img
                  src={resolvedImage}
                  alt={t.title}
                  className="absolute inset-0 w-full h-full object-cover"
                  onError={(e) => {
                    if (fallbackImage && e.currentTarget.getAttribute('src') !== fallbackImage) {
                      e.currentTarget.src = fallbackImage;
                      return;
                    }
                    e.currentTarget.style.display = 'none';
                  }}
                />
              ) : null}
            {t.is_store_item ? (
              <div className="absolute top-2 right-2">
                <Badge type="accent">Store Item</Badge>
              </div>
            ) : String(t.license || '').toUpperCase() === 'PAID' ? (
              <div className="absolute top-2 right-2">
                <Badge type="accent">Paid</Badge>
              </div>
            ) : null}
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(t.template_id); }}
              className="absolute top-2 left-2 bg-red-500/90 hover:bg-red-600 text-white p-1.5 rounded-lg"
              title="Delete template"
            >
              <Trash2 size={14} />
            </button>
            </div>
            <div className="p-4">
              <h3 className="font-bold text-gray-900">{t.title}</h3>
              {t.sub_topic && (
                <p className="text-xs text-orange-600 font-medium mt-1">{t.sub_topic}</p>
              )}
              <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                <span>{t.topic}</span>
                <span className="w-1 h-1 bg-gray-300 rounded-full"></span>
                <span className="flex items-center gap-1"><FileText size={10} /> {Number(t.downloads) || 0} views</span>
              </div>
            </div>
          </div>
          );
        })}
      </div>

      <Pagination page={currentPage} pageCount={pageCount} onChange={setPage} />

      <div className="fixed bottom-8 left-1/2 ml-12 -translate-x-1/2">
        <Button onClick={onUploadClick} className="rounded-full px-6 py-3 shadow-xl bg-[#0F5132] hover:bg-[#0b3d26]">
          <UploadCloud size={18} /> Upload File
        </Button>
      </div>
    </div>
  );
};

const UsersList = ({ users }) => {
  const [query, setQuery] = useState('');
  const [sortBy, setSortBy] = useState('downloads');

  const visible = users
    .filter((u) => {
      const q = query.trim().toLowerCase();
      if (!q) return true;
      return [u.name, u.mobile, u.phone, u.user_id].some((value) => String(value || '').toLowerCase().includes(q));
    })
    .slice()
    .sort((a, b) => {
      if (sortBy === 'name') return String(a.name || '').localeCompare(String(b.name || ''));
      if (sortBy === 'downloads-asc') return (Number(a.downloads) || 0) - (Number(b.downloads) || 0);
      if (sortBy === 'id') return String(a.user_id || '').localeCompare(String(b.user_id || ''));
      return (Number(b.downloads) || 0) - (Number(a.downloads) || 0);
    });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <h2 className="text-lg font-bold">Users ({visible.length})</h2>
        <div className="flex gap-3 flex-wrap">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter name, mobile, or id"
            className="h-8 px-3 rounded-lg border border-gray-200 text-xs w-56"
          />
          <label className="flex items-center gap-2 text-xs text-gray-500">
            Sort
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="h-8 px-2 rounded-lg border border-gray-200 bg-white text-gray-800">
              <option value="downloads">Downloads, high to low</option>
              <option value="downloads-asc">Downloads, low to high</option>
              <option value="name">Name A–Z</option>
              <option value="id">User ID</option>
            </select>
          </label>
        </div>
      </div>
      
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <table className="w-full text-left text-sm">
          <thead className="bg-white border-b border-gray-100 text-gray-500">
            <tr>
              <th className="px-6 py-4 font-normal text-xs uppercase tracking-wider">User ID</th>
              <th className="px-6 py-4 font-normal text-xs uppercase tracking-wider">Mobile</th>
              <th className="px-6 py-4 font-normal text-xs uppercase tracking-wider">Name</th>
              <th className="px-6 py-4 font-normal text-xs uppercase tracking-wider">Downloads</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {visible.map((u, i) => (
              <tr key={i} className="hover:bg-gray-50">
                <td className="px-6 py-5 font-bold text-gray-800">{u.user_id}</td>
                <td className="px-6 py-5 font-bold text-gray-800">{u.mobile || u.phone}</td>
                <td className="px-6 py-5 font-bold text-gray-800">{u.name}</td>
                <td className="px-6 py-5 font-bold text-gray-800">{u.downloads || u.downloads_made}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const DURATION_UNIT_OPTIONS = [
  { value: 'MONTHLY', label: 'Monthly', countLabel: 'months' },
  { value: 'WEEKLY', label: 'Weekly', countLabel: 'weeks' },
  { value: 'DAILY', label: 'Daily', countLabel: 'days' },
];

const durationCountLabel = (unit) =>
  DURATION_UNIT_OPTIONS.find(o => o.value === unit)?.countLabel || 'units';

const rupeesFromPaisa = (amount) => ((Number(amount) || 0) / 100).toString();
const paisaFromRupees = (rupees) => Math.round(Number(rupees) * 100);

const formatDateTime = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const emptyPlanForm = {
  name: '',
  priceRupees: '',
  durationUnit: 'MONTHLY',
  durationValue: 1,
  planType: 'BASIC',
};

const Subscription = ({ pricing, token, refresh }) => {
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyPlanForm);
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const nameInputRef = useRef(null);

  useEffect(() => {
    if ((editingId || adding) && nameInputRef.current) {
      nameInputRef.current.focus();
    }
  }, [editingId, adding]);

  const startEdit = (plan) => {
    setAdding(false);
    setEditingId(plan.id || plan._id);
    setForm({
      name: plan.name || '',
      priceRupees: rupeesFromPaisa(plan.amount),
      durationUnit: plan.duration_unit || 'MONTHLY',
      durationValue: plan.duration_value || 1,
      planType: plan.plan_type || 'BASIC',
    });
  };

  const startAdd = () => {
    setEditingId(null);
    setAdding(true);
    setForm(emptyPlanForm);
  };

  const cancelForm = () => {
    setEditingId(null);
    setAdding(false);
    setForm(emptyPlanForm);
  };

  const validateForm = () => {
    const name = form.name.trim();
    if (!name) {
      alert('Plan name is required and must be unique.');
      return null;
    }
    const amount = paisaFromRupees(form.priceRupees);
    if (!Number.isFinite(amount) || amount < 0) {
      alert('Enter a valid price in rupees.');
      return null;
    }
    const durationValue = parseInt(form.durationValue, 10);
    if (!Number.isFinite(durationValue) || durationValue < 1) {
      alert('Duration must be at least 1.');
      return null;
    }
    return {
      name,
      amount,
      duration_unit: form.durationUnit,
      duration_value: durationValue,
      plan_type: form.planType === 'PRO' ? 'PRO' : 'BASIC',
    };
  };

  const handleSave = async () => {
    const payload = validateForm();
    if (!payload) return;
    setSaving(true);
    const res = adding
      ? await apiCall('/api/v1/admin/pricing', 'POST', payload, token)
      : await apiCall(`/api/v1/admin/pricing/${editingId}`, 'PUT', payload, token);
    setSaving(false);
    if (res && res.success) {
      if (res.autopay_warning) {
        alert(res.autopay_warning);
      }
      cancelForm();
      refresh();
    } else {
      alert(res?.message || 'Failed to save plan. Please try again.');
    }
  };

  const handleDisable = async (plan) => {
    if (!window.confirm(`Disable "${plan.name}"? Customers will stop seeing it. You can delete it after ${plan.duration_label || 'the plan duration'} from today.`)) {
      return;
    }
    const res = await apiCall(`/api/v1/admin/pricing/${plan.id}/disable`, 'POST', {}, token);
    if (res && res.success) {
      refresh();
    } else {
      alert(res?.message || 'Failed to disable plan.');
    }
  };

  const handleDelete = async (plan) => {
    if (!window.confirm(`Permanently delete "${plan.name}"? This cannot be undone.`)) {
      return;
    }
    const res = await apiCall(`/api/v1/admin/pricing/${plan.id}`, 'DELETE', null, token);
    if (res && res.success) {
      refresh();
    } else {
      alert(res?.message || 'Failed to delete plan.');
    }
  };

  const renderPlanFormFields = () => (
    <>
      <div className="space-y-1">
        <label className="text-xs text-gray-500">Name (unique)</label>
        <input
          ref={nameInputRef}
          type="text"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="e.g. Premium 3 Months"
          className="w-full font-bold text-gray-800 border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-orange-200 outline-none"
        />
      </div>
      <div className="space-y-1">
        <label className="text-xs text-gray-500">Price (₹)</label>
        <input
          type="number"
          min="0"
          step="0.01"
          value={form.priceRupees}
          onChange={(e) => setForm({ ...form, priceRupees: e.target.value })}
          className="w-full font-bold text-gray-800 border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-orange-200 outline-none"
        />
      </div>
      <div className="space-y-1">
        <label className="text-xs text-gray-500">Plan type</label>
        <select
          value={form.planType}
          onChange={(e) => setForm({ ...form, planType: e.target.value })}
          className="w-full font-bold text-gray-800 border border-gray-300 rounded px-3 py-2 bg-white outline-none focus:ring-2 focus:ring-orange-200"
        >
          <option value="BASIC">Basic</option>
          <option value="PRO">Pro</option>
        </select>
      </div>
      <div className="space-y-1">
        <label className="text-xs text-gray-500">Duration</label>
        <div className="flex gap-2">
          <select
            value={form.durationUnit}
            onChange={(e) => setForm({ ...form, durationUnit: e.target.value })}
            className="flex-1 font-bold text-gray-800 border border-gray-300 rounded px-3 py-2 bg-white outline-none focus:ring-2 focus:ring-orange-200"
          >
            {DURATION_UNIT_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <input
            type="number"
            min="1"
            value={form.durationValue}
            onChange={(e) => setForm({ ...form, durationValue: e.target.value })}
            className="w-20 font-bold text-gray-800 border border-gray-300 rounded px-3 py-2 outline-none focus:ring-2 focus:ring-orange-200"
          />
        </div>
        <p className="text-[11px] text-gray-400">
          Number of {durationCountLabel(form.durationUnit)}. Autopay needs Weekly, Monthly, or Daily of 7+ days.
        </p>
      </div>
    </>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold">Subscription Plans</h2>
        {!adding && (
          <button
            onClick={startAdd}
            className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-bold px-4 py-2 rounded-lg"
          >
            <Plus size={16} />
            Add plan
          </button>
        )}
      </div>
      <div className="flex gap-6 flex-wrap">
        {adding && (
          <Card className="w-72 space-y-4 border-orange-200">
            {renderPlanFormFields()}
            <div className="flex items-center gap-3">
              <button
                onClick={handleSave}
                disabled={saving}
                className="text-orange-500 text-sm font-bold hover:text-orange-600 disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Confirm'}
              </button>
              <button onClick={cancelForm} className="text-gray-400 text-sm font-bold hover:text-gray-600">
                Cancel
              </button>
            </div>
          </Card>
        )}
        {pricing.map((plan) => {
          const planId = plan.id || plan._id;
          const isEditing = editingId === planId;
          const isDisabled = plan.is_active === false;
          return (
            <Card key={planId} className={`w-72 space-y-4 ${isDisabled ? 'opacity-80 bg-gray-50' : ''}`}>
              {isEditing ? (
                <>
                  {renderPlanFormFields()}
                  <div className="flex items-center gap-3">
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className="text-orange-500 text-sm font-bold hover:text-orange-600 disabled:opacity-50"
                    >
                      {saving ? 'Saving...' : 'Confirm'}
                    </button>
                    <button onClick={cancelForm} className="text-gray-400 text-sm font-bold hover:text-gray-600">
                      Cancel
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <label className="text-xs text-gray-500">Name</label>
                      {isDisabled && (
                        <span className="text-[10px] font-bold uppercase tracking-wide text-gray-500 bg-gray-200 px-2 py-0.5 rounded">
                          Disabled
                        </span>
                      )}
                    </div>
                    <div className="font-bold text-gray-800 border border-gray-200 rounded px-3 py-2 bg-white">
                      {plan.name}
                    </div>
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs text-gray-500">Plan type</label>
                    <div className="font-bold text-gray-800 border border-gray-200 rounded px-3 py-2 bg-white">
                      {plan.plan_type === 'PRO' ? 'Pro' : 'Basic'}
                    </div>
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs text-gray-500">Price</label>
                    <div className="font-bold text-gray-800 border border-gray-200 rounded px-3 py-2">
                      {plan.amount_display || `₹${((plan.amount || 0) / 100).toFixed(2)}`}
                    </div>
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs text-gray-500">Duration</label>
                    <div className="font-bold text-gray-800 border border-gray-200 rounded px-3 py-2">
                      {plan.duration_label || `${plan.duration_value} ${durationCountLabel(plan.duration_unit)}`}
                    </div>
                  </div>
                  <p className="text-xs text-gray-500">
                    {plan.razorpay_plan_id
                      ? `Autopay ready · ${plan.razorpay_plan_id}`
                      : 'Autopay unavailable · no Razorpay plan'}
                  </p>
                  {isDisabled && (
                    <p className="text-xs text-gray-500">
                      Disabled on {formatDateTime(plan.disabled_at) || '—'}
                      {!plan.can_delete && plan.delete_available_at && (
                        <> · Delete available {formatDateTime(plan.delete_available_at)}</>
                      )}
                    </p>
                  )}
                  <div className="flex items-center gap-3 pt-1">
                    {!isDisabled && (
                      <>
                        <button
                          onClick={() => startEdit(plan)}
                          className="text-orange-400 text-sm font-bold hover:text-orange-600 flex items-center gap-1"
                        >
                          <Pencil size={14} />
                          Edit
                        </button>
                        <button
                          onClick={() => handleDisable({ ...plan, id: planId })}
                          className="text-gray-400 text-sm font-bold hover:text-red-500 flex items-center gap-1"
                        >
                          <CircleOff size={14} />
                          Disable
                        </button>
                      </>
                    )}
                    {isDisabled && plan.can_delete && (
                      <button
                        onClick={() => handleDelete({ ...plan, id: planId })}
                        className="text-red-500 text-sm font-bold hover:text-red-600 flex items-center gap-1"
                      >
                        <Trash2 size={14} />
                        Delete
                      </button>
                    )}
                  </div>
                </>
              )}
            </Card>
          );
        })}
        {pricing.length === 0 && !adding && (
          <p className="text-sm text-gray-500">No plans yet. Add a plan to start selling subscriptions.</p>
        )}
      </div>
    </div>
  );
};

// --- MODALS ---

const ModalBackdrop = ({ children, onClose }) => (
  <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
    <div className="absolute inset-0" onClick={onClose}></div>
    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl relative z-10 animate-scale-in">
      {children}
    </div>
  </div>
);

const UploadModal = ({ onClose, onFileSelect }) => {
  const [dragActive, setDragActive] = useState(false);
  const [fileError, setFileError] = useState('');
  const inputRef = useRef(null);

  const validateAndSelect = (file) => {
    const name = file.name.toLowerCase();
    if (!name.endsWith('.ppt') && !name.endsWith('.pptx')) {
      setFileError('Only .PPT and .PPTX files are allowed. Please select a PowerPoint file.');
      return;
    }
    setFileError('');
    onFileSelect(file);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      validateAndSelect(e.dataTransfer.files[0]);
    }
  };

  return (
    <ModalBackdrop onClose={onClose}>
      <div className="p-8">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gray-100 rounded-full"><UploadCloud size={20} className="text-gray-600"/></div>
            <div>
              <h3 className="font-bold text-lg">Upload Template</h3>
              <p className="text-sm text-gray-500">Select and upload the files</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full"><X size={20} /></button>
        </div>

        <div 
          className={`border-2 border-dashed rounded-xl h-64 flex flex-col items-center justify-center gap-4 transition-colors ${dragActive ? 'border-green-500 bg-green-50' : 'border-gray-200'}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <div className="p-3 bg-gray-50 rounded-full">
            <UploadCloud size={32} className="text-gray-400" />
          </div>
          <div className="text-center">
            <p className="font-medium text-gray-700">Choose a file or drag & drop it here</p>
            <p className="text-xs text-gray-400 mt-1">Only .PPT and .PPTX formats are supported</p>
          </div>
          <input 
            ref={inputRef}
            type="file" 
            accept=".ppt,.pptx"
            className="hidden" 
            onChange={(e) => e.target.files[0] && validateAndSelect(e.target.files[0])}
          />
          <Button variant="primary" className="bg-[#1B5E3C] hover:bg-[#14472d]" onClick={() => inputRef.current?.click()}>
            Browse File
          </Button>
          {fileError && (
            <p className="text-red-500 text-sm font-medium mt-2">{fileError}</p>
          )}
        </div>
      </div>
    </ModalBackdrop>
  );
};

const TemplateDetailModal = ({ template, isNew, onClose, onBack, onSave, loading }) => {
  const [formData, setFormData] = useState({
    title: '',
    topic: '',
    license: 'FREE',
    about: '',
    is_store_item: true,
    sub_topic: '',
    ...template
  });

  useEffect(() => {
    setFormData({
      title: '',
      topic: '',
      license: 'FREE',
      about: '',
      is_store_item: true,
      sub_topic: '',
      ...template
    });
  }, [template]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <ModalBackdrop onClose={onClose}>
      <div className="p-8">
        <div className="mb-6">
          <h3 className="font-bold text-lg">{isNew ? 'New Template Details' : 'Edit Template'}</h3>
        </div>

        <div className="space-y-4">
          <Input 
            label="Project Name" 
            name="title"
            value={formData.title}
            onChange={handleChange}
          />
          
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-semibold text-gray-700">Topic</label>
            <select
              value={ADMIN_TEMPLATE_TOPIC_OPTIONS.includes(formData.topic) ? formData.topic : ''}
              onChange={(e) => {
                if (e.target.value) setFormData((prev) => ({ ...prev, topic: e.target.value }));
              }}
              className="w-full px-4 py-2.5 rounded-lg border border-gray-200 bg-white focus:outline-none text-sm text-gray-800"
            >
              <option value="">{formData.topic && !ADMIN_TEMPLATE_TOPIC_OPTIONS.includes(formData.topic) ? 'Custom topic' : 'Select a topic'}</option>
              {ADMIN_TEMPLATE_TOPIC_OPTIONS.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
            <input
              name="topic"
              value={formData.topic || ''}
              onChange={handleChange}
              placeholder="Or type any topic"
              className="w-full px-4 py-2.5 rounded-lg border border-gray-200 focus:outline-none text-sm text-gray-800"
            />
          </div>

          <div className="flex items-center justify-between gap-4 rounded-xl border border-gray-200 px-4 py-3 bg-gray-50/80">
            <span className="text-sm font-semibold text-gray-800">Mark as Store Item</span>
            <button
              type="button"
              role="switch"
              aria-checked={!!formData.is_store_item}
              onClick={() => setFormData((prev) => ({ ...prev, is_store_item: !prev.is_store_item }))}
              className={`relative inline-flex h-7 w-12 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 ${formData.is_store_item ? 'bg-orange-500' : 'bg-gray-300'}`}
            >
              <span
                className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${formData.is_store_item ? 'translate-x-5' : 'translate-x-0.5'}`}
              />
            </button>
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-semibold text-gray-700">Access</label>
            <select
              name="license"
              value={formData.is_store_item ? 'PAID' : (formData.license === 'PAID' ? 'PAID' : 'FREE')}
              disabled={!!formData.is_store_item}
              onChange={handleChange}
              className={`w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm ${formData.is_store_item ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-800'}`}
            >
              <option value="FREE">Free</option>
              <option value="PAID">Paid</option>
            </select>
            {formData.is_store_item ? (
              <p className="text-xs text-gray-400">Store items are Pro-only, so Free/Paid does not apply.</p>
            ) : null}
          </div>

          {formData.is_store_item && (
            <Input 
              label="Sub Topic" 
              name="sub_topic"
              value={formData.sub_topic || ''}
              onChange={handleChange}
              placeholder="e.g. Organic Chemistry, World War II"
            />
          )}

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-semibold text-gray-700">About the template</label>
            <textarea 
              name="about"
              rows={4}
              value={formData.about}
              onChange={handleChange}
              className="w-full px-4 py-2.5 rounded-lg border border-gray-200 focus:outline-none resize-none text-sm text-gray-600"
              placeholder="Bring science to life with this playful..."
            ></textarea>
          </div>
        </div>

        <div className="flex gap-4 mt-8">
          {isNew ? (
            <Button variant="secondary" className="flex-1" onClick={onBack} disabled={loading}>
              Edit
            </Button>
          ) : (
            <Button variant="secondary" className="flex-1" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
          )}
          <Button
            variant="primary"
            className="flex-1 bg-[#2E6B47]"
            onClick={() => onSave(formData)}
            disabled={loading || !String(formData.topic || '').trim()}
          >
             {loading ? 'Saving…' : isNew ? 'Save Changes' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </ModalBackdrop>
  );
};